import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BrandService } from '@app/services/brand.service';
import { environment } from '@src/environments/environment';

export interface BrandRewriteConfig {
  apiUrl: string;
  uploadsApiUrl: string;
  carpeakApiUrl?: string;
}

export const BRAND_REWRITE_CONFIG = new InjectionToken<BrandRewriteConfig>('BRAND_REWRITE_CONFIG', {
  factory: () => ({
    apiUrl: environment.apiUrl,
    uploadsApiUrl: environment.uploadsApiUrl,
    carpeakApiUrl: (environment as { carpeakApiUrl?: string }).carpeakApiUrl,
  }),
});

@Injectable()
export class BrandUrlRewriteInterceptor implements HttpInterceptor {
  private readonly replacements: ReadonlyArray<[string, string]>;

  constructor(private brandService: BrandService) {
    const config = inject(BRAND_REWRITE_CONFIG);

    if (!config.carpeakApiUrl) {
      this.replacements = [];
      return;
    }

    const carpeakDomain = this.extractDomain(config.carpeakApiUrl);
    const apiDomain = this.extractDomain(config.apiUrl);
    const uploadsDomain = this.extractDomain(config.uploadsApiUrl);

    // Use a Map to deduplicate (beta: apiDomain === uploadsDomain === 'briefd.io')
    const pairs = new Map<string, string>();
    pairs.set(`.${apiDomain}`, `.${carpeakDomain}`);
    pairs.set(`.${uploadsDomain}`, `.${carpeakDomain}`);

    this.replacements = Array.from(pairs.entries());
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.brandService.current !== 'carpeak' || this.replacements.length === 0) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      map(event => {
        if (!(event instanceof HttpResponse) || event.body === null || typeof event.body !== 'object') {
          return event;
        }

        let json = JSON.stringify(event.body);
        for (const [from, to] of this.replacements) {
          // Only rewrite upload/image URLs — signed URLs (e.g. space-invoices) include the host in
          // their HMAC so rewriting the domain breaks Laravel's hasValidSignature() check.
          const escapedFrom = from.replace(/\./g, '\\.');
          const uploadUrlRegex = new RegExp(`https?://[^"]*${escapedFrom}/api/uploads/[^"]*`, 'g');
          json = json.replace(uploadUrlRegex, (match) => match.replace(from, to));
        }

        return event.clone({ body: JSON.parse(json) });
      }),
    );
  }

  private extractDomain(url: string): string {
    return new URL(url).hostname.split('.').slice(-2).join('.');
  }
}
